// 用户信息接口（auth-service / base-service 都暴露 /user/userinfor）。
// summary.json 显示路径存在，无完整抓包样本，字段按 ulucu 命名风格保守推断。
import { callInternal } from './internal.js';

export interface UlucuUserInfo {
	user_id?: string;
	customer_id?: string;
	b_auto_id?: string | number;
	account?: string;
	realname?: string;
	name?: string;            // 昵称
	nick_name?: string;       // 同上（ulucu 老接口拼写）
	mobile?: string;          // 手机
	phone?: string;           // 同上（ulucu 字段不一致，两个都兜）
	email?: string;
	avatar?: string;          // 头像 URL
	head_url?: string;        // 同上
	[k: string]: unknown;
}

/** 拉当前登录用户信息。后端 token 已带，无需额外参数。 */
export async function fetchUserInfo(): Promise<UlucuUserInfo | null> {
	// auth 优先（access/login 返了 user_id 后通常配套用 auth/user/userinfor），
	// 不通时降级到 base
	for (const short_name of ['auth', 'base']) {
		try {
			const res = await callInternal<UlucuUserInfo>({
				short_name,
				action: '/user/userinfor',
				method: 'GET',
			});
			if (res.code === 0 && res.data) return res.data;
		} catch { /* 下个 short_name 再试 */ }
	}
	return null;
}

/** 字段别名兜底：name / nick_name 任一存在 → 返回；mobile / phone 同理 */
export function pickName(u: UlucuUserInfo | null | undefined): string | undefined {
	return u?.realname || u?.name || u?.nick_name;
}
export function pickMobile(u: UlucuUserInfo | null | undefined): string | undefined {
	return u?.mobile || u?.phone;
}
export function pickAvatar(u: UlucuUserInfo | null | undefined): string | undefined {
	return u?.avatar || u?.head_url;
}

export interface UserProfileUpdate {
	realname?: string;
	mobile?: string;
	email?: string;
	avatar?: string;
}

type UserProfileRegion = 'cn' | 'apac';

export async function saveUserProfile(update: UserProfileUpdate, region: UserProfileRegion = 'cn'): Promise<{ ok: boolean; msg?: string }> {
	try {
		if (region !== 'apac') {
			const body: Record<string, string> = {};
			if (update.realname != null) body.realname = update.realname;
			if (update.mobile != null) body.mobile = update.mobile;
			if (update.email != null) body.email = update.email;
			if (update.avatar != null) body.avatar = update.avatar;
			if (Object.keys(body).length === 0) return { ok: true, msg: 'ok' };
			const res = await editSelf(body);
			const ok = Number(res.code) === 0;
			return { ok, msg: ok ? res.msg || 'ok' : res.msg || `code=${res.code}` };
		}

		const profileBody: Record<string, string> = {};
		if (update.realname != null) profileBody.real_name = update.realname;
		if (update.mobile != null) profileBody.mobile = update.mobile;
		if (update.email != null) profileBody.email = update.email;
		const hasProfileBody = Object.keys(profileBody).length > 0;
		let lastMsg: string | undefined;
		if (hasProfileBody) {
			const profileRes = await editSelf(profileBody);
			if (Number(profileRes.code) !== 0) {
				return { ok: false, msg: profileRes.msg || `code=${profileRes.code}` };
			}
			lastMsg = profileRes.msg;
		}
		if (update.avatar != null) {
			const avatarRes = await editSelf({ avatar: update.avatar });
			if (Number(avatarRes.code) !== 0) {
				return { ok: false, msg: avatarRes.msg || `code=${avatarRes.code}` };
			}
			lastMsg = avatarRes.msg || lastMsg;
		}
		return { ok: true, msg: lastMsg || 'ok' };
	} catch (e) {
		return { ok: false, msg: e instanceof Error ? e.message : String(e) };
	}
}

function editSelf(body: Record<string, string>): Promise<{ code: number; msg?: string }> {
	return callInternal({
		short_name: 'base',
		action: '/user/edit_self',
		method: 'POST',
		body,
		av: '1',
	});
}
